/**
 * 订单状态统一映射中心
 *
 * 架构原则：
 * 1. 源状态（后端真实状态）是唯一可信来源
 * 2. 展示状态、Tab归类、操作规则都是源状态的派生
 * 3. 一对多映射：一个源状态 → 多角色不同表现
 * 4. 禁止页面直接判断 statusText === 'xxx' 来做业务逻辑
 */

// ==================== 1. 源状态定义 ====================

/**
 * 源状态列表（后端真实状态）
 * 这是唯一可信的业务状态来源
 */
export const SOURCE_STATUS = {
  PENDING_CONFIRM: 'pending_confirm', // 待确认
  PENDING_PICKING: 'pending_picking', // 待配货
  PICKED: 'picked', // 已配货
  INSPECTED: 'inspected', // 已验货
  // @deprecated legacy status — shipped 已废弃，新数据不再进入此状态。保留常量仅为历史数据兼容。
  SHIPPED: 'shipped', // 已发货/待配送
  DELIVERING: 'delivering', // 配送中
  DELIVERED: 'delivered', // 已送达（待用户确认）
  CONFIRMED: 'confirmed', // 已确认/已完成
  CANCELLED: 'cancelled' // 已取消
}

/**
 * 源状态显示文本（内部标识用，不直接展示给用户）
 */
export const SOURCE_STATUS_TEXT = {
  [SOURCE_STATUS.PENDING_CONFIRM]: '待确认',
  [SOURCE_STATUS.PENDING_PICKING]: '待配货',
  [SOURCE_STATUS.PICKED]: '已配货',
  [SOURCE_STATUS.INSPECTED]: '已验货',
  [SOURCE_STATUS.SHIPPED]: '已发货',
  [SOURCE_STATUS.DELIVERING]: '配送中',
  [SOURCE_STATUS.DELIVERED]: '已送达（待用户确认）',
  [SOURCE_STATUS.CONFIRMED]: '已完成',
  [SOURCE_STATUS.CANCELLED]: '已取消'
}

// ==================== 2. 角色定义 ====================

export const ROLES = {
  USER: 'user', // 普通用户
  ADMIN: 'admin', // 管理员
  COURIER: 'courier', // 配送员
  OPERATOR: 'operator', // 业务员
  SUPER_ADMIN: 'super_admin' // 超级管理员
}

// ==================== 3. 一对多映射表 ====================

/**
 * 展示状态映射：源状态 → 各角色展示文案
 */
export const DISPLAY_STATUS_MAP = {
  [SOURCE_STATUS.PENDING_CONFIRM]: {
    [ROLES.USER]: '待确认',
    [ROLES.ADMIN]: '待确认',
    [ROLES.COURIER]: '-',
    [ROLES.OPERATOR]: '待处理'
  },
  [SOURCE_STATUS.PENDING_PICKING]: {
    [ROLES.USER]: '准备中',
    [ROLES.ADMIN]: '待配货',
    [ROLES.COURIER]: '-',
    [ROLES.OPERATOR]: '待处理'
  },
  [SOURCE_STATUS.PICKED]: {
    [ROLES.USER]: '准备中',
    [ROLES.ADMIN]: '已配货',
    [ROLES.COURIER]: '-',
    [ROLES.OPERATOR]: '待验货'
  },
  [SOURCE_STATUS.INSPECTED]: {
    [ROLES.USER]: '待配送',
    [ROLES.ADMIN]: '已验货',
    [ROLES.COURIER]: '待配送',
    [ROLES.OPERATOR]: '待配送'
  },
  [SOURCE_STATUS.SHIPPED]: {
    [ROLES.USER]: '待配送',
    [ROLES.ADMIN]: '待配送',
    [ROLES.COURIER]: '待配送',
    [ROLES.OPERATOR]: '验货中'
  },
  [SOURCE_STATUS.DELIVERING]: {
    [ROLES.USER]: '配送中',
    [ROLES.ADMIN]: '配送中',
    [ROLES.COURIER]: '配送中',
    [ROLES.OPERATOR]: '配送中'
  },
  [SOURCE_STATUS.DELIVERED]: {
    [ROLES.USER]: '需确认收货',
    [ROLES.ADMIN]: '已送达/待用户确认',
    [ROLES.COURIER]: '已送达',
    [ROLES.OPERATOR]: '已送达'
  },
  [SOURCE_STATUS.CONFIRMED]: {
    [ROLES.USER]: '已完成',
    [ROLES.ADMIN]: '已完成',
    [ROLES.COURIER]: '已完成',
    [ROLES.OPERATOR]: '已完成'
  },
  [SOURCE_STATUS.CANCELLED]: {
    [ROLES.USER]: '已取消',
    [ROLES.ADMIN]: '已取消',
    [ROLES.COURIER]: '已取消',
    [ROLES.OPERATOR]: '已取消'
  }
}

/**
 * Tab归类映射：源状态 → 各角色Tab分类
 *
 * Tab分类不是业务状态，而是页面分栏归类
 */
export const TAB_CATEGORY_MAP = {
  [SOURCE_STATUS.PENDING_CONFIRM]: {
    [ROLES.USER]: 'pending',
    [ROLES.ADMIN]: 'pending',
    [ROLES.COURIER]: '-',
    [ROLES.OPERATOR]: 'pending'
  },
  [SOURCE_STATUS.PENDING_PICKING]: {
    [ROLES.USER]: 'pending',
    [ROLES.ADMIN]: 'processing',
    [ROLES.COURIER]: '-',
    [ROLES.OPERATOR]: 'pending'
  },
  [SOURCE_STATUS.PICKED]: {
    [ROLES.USER]: 'pending',
    [ROLES.ADMIN]: 'processing',
    [ROLES.COURIER]: '-',
    [ROLES.OPERATOR]: 'pending'
  },
  [SOURCE_STATUS.INSPECTED]: {
    [ROLES.USER]: 'pending',
    [ROLES.ADMIN]: 'ready',
    [ROLES.COURIER]: 'pending', // 配送员：待接单/待配送
    [ROLES.OPERATOR]: 'pending' // 业务员：待选配送/待我开始配送
  },
  [SOURCE_STATUS.SHIPPED]: {
    [ROLES.USER]: 'pending',
    [ROLES.ADMIN]: 'ready',
    [ROLES.COURIER]: 'pending',
    [ROLES.OPERATOR]: 'pending'
  },
  [SOURCE_STATUS.DELIVERING]: {
    [ROLES.USER]: 'shipped', // 用户端：配送中
    [ROLES.ADMIN]: 'transporting',
    [ROLES.COURIER]: 'delivering',
    [ROLES.OPERATOR]: 'delivering'
  },
  [SOURCE_STATUS.DELIVERED]: {
    [ROLES.USER]: 'pending', // 重点：用户端归类到待处理！
    [ROLES.ADMIN]: 'transporting', // 管理员：运输中/待闭环
    [ROLES.COURIER]: 'delivered',
    [ROLES.OPERATOR]: 'completed'
  },
  [SOURCE_STATUS.CONFIRMED]: {
    [ROLES.USER]: 'completed',
    [ROLES.ADMIN]: 'completed',
    [ROLES.COURIER]: 'completed',
    [ROLES.OPERATOR]: 'completed'
  },
  [SOURCE_STATUS.CANCELLED]: {
    [ROLES.USER]: 'cancelled',
    [ROLES.ADMIN]: 'cancelled',
    [ROLES.COURIER]: 'cancelled',
    [ROLES.OPERATOR]: 'cancelled'
  }
}

/**
 * Tab配置：各角色支持的Tab列表
 */
export const TAB_CONFIG = {
  [ROLES.USER]: [
    { value: 'all', label: '全部' },
    { value: 'pending', label: '待处理' },
    { value: 'shipped', label: '配送中' },
    { value: 'completed', label: '已完成' },
    { value: 'cancelled', label: '历史' }
  ],
  [ROLES.ADMIN]: [
    { value: 'all', label: '全部' },
    { value: 'pending', label: '待处理' },
    { value: 'processing', label: '处理中' },
    { value: 'ready', label: '待履约' },
    { value: 'transporting', label: '运输中' },
    { value: 'completed', label: '已完成' },
    { value: 'cancelled', label: '已取消' }
  ],
  [ROLES.COURIER]: [
    { value: 'all', label: '全部' },
    { value: 'pending', label: '待配送' },
    { value: 'delivering', label: '配送中' },
    { value: 'delivered', label: '已送达' },
    { value: 'completed', label: '已完成' }
  ],
  [ROLES.OPERATOR]: [
    { value: 'all', label: '全部' },
    { value: 'pending', label: '待处理' },
    { value: 'processing', label: '处理中' },
    { value: 'delivering', label: '配送中' },
    { value: 'completed', label: '已完成' },
    { value: 'cancelled', label: '历史' }
  ]
}

/**
 * Tab优先级：用于自动聚焦（数字越小优先级越高）
 */
export const TAB_PRIORITY = {
  [ROLES.USER]: ['pending', 'shipped', 'completed', 'cancelled'],
  [ROLES.ADMIN]: ['pending', 'processing', 'ready', 'transporting', 'completed', 'cancelled'],
  [ROLES.COURIER]: ['pending', 'delivering', 'delivered', 'completed'],
  [ROLES.OPERATOR]: ['pending', 'processing', 'delivering', 'completed', 'cancelled']
}

// ==================== 4. 操作规则映射 ====================

/**
 * 可操作性映射：源状态 → 各角色允许的操作
 *
 * 操作类型：
 * - ship: 发货
 * - assign: 指派配送员
 * - inspect: 验货
 * - start_delivery: 开始配送
 * - mark_delivered: 标记送达
 * - confirm_receipt: 确认收货
 * - cancel: 取消订单
 * - edit_status: 修改状态（管理员）
 */
export const ACTION_RULES = {
  // 待确认
  [SOURCE_STATUS.PENDING_CONFIRM]: {
    [ROLES.ADMIN]: ['inspect', 'assign', 'cancel', 'edit_status'],
    [ROLES.USER]: ['cancel'],
    [ROLES.COURIER]: [],
    [ROLES.OPERATOR]: ['inspect']
  },
  // 待配货
  [SOURCE_STATUS.PENDING_PICKING]: {
    [ROLES.ADMIN]: ['inspect', 'cancel', 'edit_status'],
    [ROLES.USER]: [],
    [ROLES.COURIER]: [],
    [ROLES.OPERATOR]: ['inspect']
  },
  // 已配货
  [SOURCE_STATUS.PICKED]: {
    [ROLES.ADMIN]: ['inspect', 'assign', 'cancel', 'edit_status'],
    [ROLES.USER]: [],
    [ROLES.COURIER]: [],
    [ROLES.OPERATOR]: ['inspect']
  },
  // 已验货
  [SOURCE_STATUS.INSPECTED]: {
    [ROLES.ADMIN]: ['ship', 'assign', 'cancel', 'edit_status'],
    [ROLES.USER]: [],
    [ROLES.COURIER]: ['start_delivery'],
    [ROLES.OPERATOR]: ['start_delivery']
  },
  // 已发货
  [SOURCE_STATUS.SHIPPED]: {
    [ROLES.ADMIN]: ['assign', 'cancel', 'edit_status'],
    [ROLES.USER]: [],
    [ROLES.COURIER]: ['accept'], // 接单
    [ROLES.OPERATOR]: ['inspect']
  },
  // 配送中
  [SOURCE_STATUS.DELIVERING]: {
    [ROLES.ADMIN]: ['edit_status'], // 可手动改状态
    [ROLES.USER]: [],
    [ROLES.COURIER]: ['mark_delivered'],
    [ROLES.OPERATOR]: ['mark_delivered']
  },
  // 已送达（待用户确认）
  [SOURCE_STATUS.DELIVERED]: {
    [ROLES.ADMIN]: ['edit_status'], // 可强制改状态
    [ROLES.USER]: ['confirm_receipt'], // 用户可确认收货
    [ROLES.COURIER]: [],
    [ROLES.OPERATOR]: []
  },
  // 已完成
  [SOURCE_STATUS.CONFIRMED]: {
    [ROLES.ADMIN]: ['edit_status'], // 是否允许改回，按产品规则
    [ROLES.USER]: [],
    [ROLES.COURIER]: [],
    [ROLES.OPERATOR]: []
  },
  // 已取消
  [SOURCE_STATUS.CANCELLED]: {
    [ROLES.ADMIN]: ['edit_status'], // 是否允许恢复
    [ROLES.USER]: [],
    [ROLES.COURIER]: [],
    [ROLES.OPERATOR]: []
  }
}

// ==================== 5. 管理员状态下拉规则 ====================

/**
 * 管理员状态下拉选项
 * 这是修改源状态的下拉
 */
export const ADMIN_STATUS_OPTIONS = [
  { value: SOURCE_STATUS.PENDING_CONFIRM, label: '待确认' },
  { value: SOURCE_STATUS.PENDING_PICKING, label: '待配货' },
  { value: SOURCE_STATUS.PICKED, label: '已配货' },
  { value: SOURCE_STATUS.SHIPPED, label: '验货中' },
  { value: SOURCE_STATUS.INSPECTED, label: '已验货' },
  { value: SOURCE_STATUS.DELIVERING, label: '配送中' },
  { value: SOURCE_STATUS.DELIVERED, label: '已送达（待用户确认）' },
  { value: SOURCE_STATUS.CONFIRMED, label: '已完成' },
  { value: SOURCE_STATUS.CANCELLED, label: '已取消' }
]

/**
 * 状态流转规则（可选限制）
 * null 表示不限制，允许所有状态间切换
 */
export const STATUS_TRANSITION_RULES = null // 产品要求管理员可任意改状态

// ==================== 6. 统一派生函数 ====================

/**
 * pickup 文案覆盖：验货是自提完成分界线，状态文案随履约模式而变（与 server/utils/fulfillment-rules.js 对齐）
 * 仅覆盖 pickup 语义不同的两格，其余走角色映射；非 pickup 单零变化
 */
const PICKUP_STATUS_TEXT = {
  [SOURCE_STATUS.PENDING_CONFIRM]: '待验货',
  [SOURCE_STATUS.INSPECTED]: '等待自提'
}

/**
 * 获取展示状态
 * @param {string} sourceStatus - 源状态
 * @param {string} role - 角色
 * @param {string} [mode] - 履约模式 courier/express/pickup
 * @returns {string} 展示文案
 */
export function getDisplayStatus(sourceStatus, role = ROLES.USER, mode) {
  if (mode === 'pickup' && PICKUP_STATUS_TEXT[sourceStatus]) return PICKUP_STATUS_TEXT[sourceStatus]
  const map = DISPLAY_STATUS_MAP[sourceStatus]
  if (!map) return sourceStatus
  return map[role] || map[ROLES.USER] || sourceStatus
}

/**
 * 获取Tab归类
 * @param {string} sourceStatus - 源状态
 * @param {string} role - 角色
 * @returns {string} Tab分类值
 */
export function getTabCategory(sourceStatus, role = ROLES.USER) {
  const map = TAB_CATEGORY_MAP[sourceStatus]
  if (!map) return 'all'
  return map[role] || 'all'
}

/**
 * 获取可用操作列表
 * @param {string} sourceStatus - 源状态
 * @param {string} role - 角色
 * @returns {string[]} 操作列表
 */
export function getAvailableActions(sourceStatus, role = ROLES.USER) {
  const rules = ACTION_RULES[sourceStatus]
  if (!rules) return []
  return rules[role] || []
}

/**
 * 检查是否允许特定操作
 * @param {string} sourceStatus - 源状态
 * @param {string} action - 操作类型
 * @param {string} role - 角色
 * @returns {boolean}
 */
export function canPerformAction(sourceStatus, action, role = ROLES.USER) {
  const actions = getAvailableActions(sourceStatus, role)
  return actions.includes(action)
}

// ==================== 4.5 配送动作归属判定（单一判断点） ====================

/**
 * 我是不是这一单的配送负责人。
 * 配送是「行为」不是「角色」：admin / operator 都可以自送（2026-09-24 口径）。
 * 已指派配送负责人 → 只有该人能送；未指派 → 该单处理人（operator_id=me）即接手自送者。
 */
export function isDeliveryOwner(order, userId) {
  if (!order || !userId) return false
  const courier = order.assigned_courier_id
  if (courier) return String(courier) === String(userId)
  return String(order.operator_id || '') === String(userId)
}

/**
 * 角色是否有配送能力（user 永不可配送；其余三形态都可能，最终由归属判定收口）
 */
function roleCanDeliver(order, role) {
  if (role === ROLES.USER) return false
  if (role === ROLES.COURIER || role === ROLES.OPERATOR || role === ROLES.ADMIN) return true
  // 角色缺失（老数据/未登录态）：退回按订单事实判断归属，不额外放行
  return !!role
}

/**
 * 开始配送：状态已验货 + 非自提单 + 我是配送负责人。
 * 卡片与详情共用本函数——不得再在页面里手写 assigned_courier_id === userId 之类的条件。
 */
export function canStartDelivery(order, role = ROLES.USER, userId = null) {
  if (!order || !roleCanDeliver(order, role)) return false
  const sourceStatus = order.status || order.source_status
  if (sourceStatus !== SOURCE_STATUS.INSPECTED) return false
  if (order.fulfillment_mode === 'pickup') return false
  return isDeliveryOwner(order, userId)
}

/**
 * 标记送达：配送中 + 非自提单 + 我是配送负责人
 */
export function canMarkDelivered(order, role = ROLES.USER, userId = null) {
  if (!order || !roleCanDeliver(order, role)) return false
  const sourceStatus = order.status || order.source_status
  if (sourceStatus !== SOURCE_STATUS.DELIVERING) return false
  if (order.fulfillment_mode === 'pickup') return false
  return isDeliveryOwner(order, userId)
}

/**
 * 获取角色对应的Tab列表
 * @param {string} role - 角色
 * @returns {Array} Tab配置列表
 */
export function getTabOptions(role = ROLES.USER) {
  return TAB_CONFIG[role] || TAB_CONFIG[ROLES.USER]
}

/**
 * 根据订单列表统计各Tab数量
 * @param {Array} orders - 订单列表
 * @param {string} role - 角色
 * @returns {Object} 各Tab数量统计
 */
export function calculateTabCounts(orders, role = ROLES.USER) {
  const tabs = getTabOptions(role)
  const counts = { all: orders.length }

  // 初始化各Tab计数为0
  tabs.forEach(tab => {
    if (tab.value !== 'all') {
      counts[tab.value] = 0
    }
  })

  // 统计每个订单归属的Tab
  orders.forEach(order => {
    const sourceStatus = order.status || order.source_status
    const tabCategory = getTabCategory(sourceStatus, role)
    if (counts[tabCategory] !== undefined) {
      counts[tabCategory]++
    }
  })

  return counts
}

/**
 * 根据优先级自动选择Tab
 * @param {Object} tabCounts - Tab数量统计
 * @param {string} role - 角色
 * @param {string} currentTab - 当前Tab（避免重复切换）
 * @returns {string|null} 建议切换的Tab，null表示不需要切换
 */
export function suggestTabByPriority(tabCounts, role = ROLES.USER, currentTab = null) {
  const priority = TAB_PRIORITY[role] || TAB_PRIORITY[ROLES.USER]

  // 找到第一个有订单的Tab
  for (const tabValue of priority) {
    if (tabCounts[tabValue] > 0) {
      // 如果当前已经在该Tab，不需要切换
      if (currentTab === tabValue) return null
      return tabValue
    }
  }

  return null
}

/**
 * 标准化订单数据（用于列表渲染）
 * @param {Object} order - 原始订单数据
 * @param {string} role - 角色
 * @param {string} userId - 当前用户 id（判配送归属用；不传则退回角色表口径，行为与修复前一致）
 * @returns {Object} 派生后的订单数据
 */
export function normalizeOrder(order, role = ROLES.USER, userId = null) {
  const sourceStatus = order.status || order.source_status || SOURCE_STATUS.PENDING_CONFIRM

  return {
    ...order,

    // 源状态（唯一可信来源）
    _sourceStatus: sourceStatus,

    // 派生：展示状态（pickup 单文案随模式覆盖：待验货/等待自提）
    _displayStatus: getDisplayStatus(sourceStatus, role, order.fulfillment_mode),

    // 派生：Tab归类
    _tabCategory: getTabCategory(sourceStatus, role),

    // 派生：可用操作
    _availableActions: getAvailableActions(sourceStatus, role),

    // 派生：是否允许特定操作（快捷方法）
    _canShip: canPerformAction(sourceStatus, 'ship', role),
    _canAssign: canPerformAction(sourceStatus, 'assign', role),
    _showAssignButton: getAvailableActions(sourceStatus, role).includes('assign') && role === ROLES.ADMIN,
    _canInspect: canPerformAction(sourceStatus, 'inspect', role),
    // 配送两动作走归属感知判定（配送是行为不是角色：admin 自送也须能开始/送达）。
    // 未传 userId 的调用方退回角色表，行为与修复前逐字相同。
    _canStartDelivery: userId
      ? canStartDelivery(order, role, userId)
      : canPerformAction(sourceStatus, 'start_delivery', role),
    _canMarkDelivered: userId
      ? canMarkDelivered(order, role, userId)
      : canPerformAction(sourceStatus, 'mark_delivered', role),

    _canConfirmReceipt: canPerformAction(sourceStatus, 'confirm_receipt', role),
    _canEditStatus: canPerformAction(sourceStatus, 'edit_status', role),

    // 派生：是否为最终态（用于禁用某些操作）
    _isFinalState: [SOURCE_STATUS.CONFIRMED, SOURCE_STATUS.CANCELLED].includes(sourceStatus),

    // 派生：是否需要用户确认（特殊状态）
    _awaitingUserConfirm: sourceStatus === SOURCE_STATUS.DELIVERED
  }
}

/**
 * 标准化订单列表
 * @param {Array} orders - 原始订单列表
 * @param {string} role - 角色
 * @param {string} userId - 当前用户 id（同 normalizeOrder）
 * @returns {Array} 派生后的订单列表
 */
export function normalizeOrders(orders, role = ROLES.USER, userId = null) {
  return orders.map(order => normalizeOrder(order, role, userId))
}

/**
 * 过滤指定Tab的订单
 * @param {Array} normalizedOrders - 已标准化的订单列表
 * @param {string} tabValue - Tab值
 * @returns {Array} 过滤后的订单列表
 */
export function filterOrdersByTab(normalizedOrders, tabValue) {
  if (tabValue === 'all' || !tabValue) return normalizedOrders
  return normalizedOrders.filter(order => order._tabCategory === tabValue)
}

// ==================== 7. 打包信息相关 ====================

/**
 * 打包信息字段定义
 */
export const PACKING_INFO = {
  unit: {
    type: 'enum',
    options: [
      { value: 'box', label: '箱' },
      { value: 'bag', label: '袋' }
    ],
    nullable: true
  },
  count: {
    type: 'integer',
    min: 1,
    nullable: true
  }
}

/**
 * 格式化打包信息为展示文本
 * @param {Object} packingInfo - { unit, count }
 * @returns {string|null} 格式化后的文本，无效时返回null
 */
export function formatPackingInfo(packingInfo) {
  if (!packingInfo) return null

  const { unit, count } = packingInfo

  // 单位和件数必须同时存在才有效
  if (!unit || !count) return null

  const unitMap = { box: '箱', bag: '袋' }
  const unitText = unitMap[unit] || unit

  return `${count} ${unitText}`
}

/**
 * 验证打包信息
 * @param {Object} packingInfo - { unit, count }
 * @returns {Object} { valid: boolean, error?: string }
 */
export function validatePackingInfo(packingInfo) {
  // 都为空视为有效（可选字段）
  if (!packingInfo) return { valid: true }

  const { unit, count } = packingInfo

  // 都为空视为有效
  if (!unit && !count) return { valid: true }

  // 只填一个视为无效
  if (unit && !count) {
    return { valid: false, error: '请填写包装件数' }
  }
  if (!unit && count) {
    return { valid: false, error: '请选择包装单位' }
  }

  // 件数必须为正整数
  const countNum = parseInt(count, 10)
  if (isNaN(countNum) || countNum < 1) {
    return { valid: false, error: '包装件数必须为正整数' }
  }

  // 单位必须是有效值
  const validUnits = ['box', 'bag']
  if (!validUnits.includes(unit)) {
    return { valid: false, error: '无效的包装单位' }
  }

  return { valid: true }
}

// ==================== 8. 兼容性导出（用于渐进式迁移） ====================

/**
 * 兼容旧代码的状态判断
 * 已废弃，请使用新的派生属性
 */
export const legacyCompat = {
  // 旧代码中常见的错误判断方式及其正确替代

  /**
   * 错误：order.status === 'delivered' && order.completed
   * 正确：order._sourceStatus === 'delivered'
   */

  /**
   * 检查订单是否已完成（最终态）
   */
  isCompleted(order) {
    return order._sourceStatus === SOURCE_STATUS.CONFIRMED ||
           order._sourceStatus === SOURCE_STATUS.CANCELLED
  },

  /**
   * 检查订单是否需要用户确认
   */
  isAwaitingUserConfirm(order) {
    return order._sourceStatus === SOURCE_STATUS.DELIVERED
  }
}

// ==================== 默认导出 ====================

export default {
  SOURCE_STATUS,
  SOURCE_STATUS_TEXT,
  ROLES,
  DISPLAY_STATUS_MAP,
  TAB_CATEGORY_MAP,
  TAB_CONFIG,
  TAB_PRIORITY,
  ACTION_RULES,
  ADMIN_STATUS_OPTIONS,
  PACKING_INFO,

  getDisplayStatus,
  getTabCategory,
  getAvailableActions,
  canPerformAction,
  isDeliveryOwner,
  canStartDelivery,
  canMarkDelivered,
  getTabOptions,
  calculateTabCounts,
  suggestTabByPriority,
  normalizeOrder,
  normalizeOrders,
  filterOrdersByTab,
  formatPackingInfo,
  validatePackingInfo,

  legacyCompat
}
